import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import type {EmployeeUserResponse, ServiceModuleModel} from '@/api/model'
import {PageLayout} from '@/components/layout/PageLayout'
import {PageHeader} from '@/components/layout/PageHeader'
import {PageHeaderActions} from '@/components/layout/PageHeaderActions'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/shadcn/tabs'
import {HealthInsuranceOverviewTable} from './health/components/HealthInsuranceOverviewTable.tsx'
import {HealthInsuranceEmployeeDialog} from './health/components/HealthInsuranceEmployeeDialog.tsx'
import {HealthInsuranceImportButton} from './health/header-actions-components/HealthInsuranceImportButton'
import {HealthInsuranceExportButton} from './health/header-actions-components/HealthInsuranceExportButton'
import {PensionInsuranceImportButton} from './pension/header-actions-components/PensionInsuranceImportButton'
import {PensionInsuranceExportButton} from './pension/header-actions-components/PensionInsuranceExportButton'
import {useHealthInsuranceConfigs} from './health/hooks/useHealthInsuranceConfigs.ts'
import {  PensionInsuranceOverviewTable} from './pension/components/PensionInsuranceOverviewTable.tsx'
import {  PensionInsuranceEmployeeDialog} from './pension/components/PensionInsuranceEmployeeDialog.tsx'
import {  usePensionInsuranceConfigs} from './pension/hooks/usePensionInsuranceConfigs.ts'

interface Props {
  module: ServiceModuleModel
}

export function InsuranceServiceModulePage({module}: Props) {
  const {t} = useTranslation('service-modules-insurance')
  const [tab, setTab] = useState('health')
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeUserResponse | null>(null)
  const {healthConfigsByEmployeeId, refetchHealthConfigs} = useHealthInsuranceConfigs(module.id)
  const {pensionConfigsByEmployeeId, refetchPensionConfigs} = usePensionInsuranceConfigs(module.id)

  return (
    <PageLayout>
      <PageHeader title={module.name || t('insurance.title')} description={t('insurance.description')}>
        {tab === 'health' && (
          <PageHeaderActions>
            <HealthInsuranceImportButton
              serviceModuleId={module.id}
              companyId={module.companyId}
              onSuccess={refetchHealthConfigs}
            />
            <HealthInsuranceExportButton
              serviceModuleId={module.id}
            />
          </PageHeaderActions>
        )}
        {tab === 'pension' && (
          <PageHeaderActions>
            <PensionInsuranceImportButton
              serviceModuleId={module.id}
              companyId={module.companyId}
              onSuccess={refetchPensionConfigs}
            />
            <PensionInsuranceExportButton
              serviceModuleId={module.id}
            />
          </PageHeaderActions>
        )}
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
        <TabsList>
          <TabsTrigger value="health">{t('insurance.tabs.health')}</TabsTrigger>
          <TabsTrigger value="pension">{t('insurance.tabs.pension')}</TabsTrigger>
        </TabsList>

        <TabsContent value="health" className="min-h-0 flex flex-col mt-4">
          <HealthInsuranceOverviewTable
            module={module}
            onEdit={(employee) => setSelectedEmployee(employee)}
          />
        </TabsContent>

        <TabsContent value="pension" className="min-h-0 flex flex-col mt-4">
          <PensionInsuranceOverviewTable
            module={module}
            onEdit={(employee) => setSelectedEmployee(employee)}
          />
        </TabsContent>
      </Tabs>

      <HealthInsuranceEmployeeDialog
        open={selectedEmployee !== null}
        onOpenChange={(open) => !open && setSelectedEmployee(null)}
        employee={selectedEmployee}
        serviceModuleId={module.id}
        companyId={module.companyId}
        config={selectedEmployee?.id ? healthConfigsByEmployeeId.get(selectedEmployee.id) : undefined}
        onSuccess={refetchHealthConfigs}
      />
      <PensionInsuranceEmployeeDialog
        open={selectedEmployee !== null}
        onOpenChange={(open) => !open && setSelectedEmployee(null)}
        employee={selectedEmployee}
        serviceModuleId={module.id}
        companyId={module.companyId}
        config={selectedEmployee?.id ? pensionConfigsByEmployeeId.get(selectedEmployee.id) : undefined}
        onSuccess={refetchPensionConfigs}
      />
    </PageLayout>
  )
}
